import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addCatalogoSatCommonKey,
  getCatalogoSatCommonKeys,
  isCatalogoSatQueryValid,
  removeCatalogoSatCommonKey,
  searchCatalogoSat,
  searchCatalogoSatClients
} from '../services/catalogoSatApi';
import '../styles/views-catalogo-sat.css';

const DEBOUNCE_MS = 275;

function errorMessage(error) {
  const messages = {
    auth: 'Tu sesión terminó. Inicia sesión nuevamente.',
    forbidden: 'Tu sesión no puede realizar esta operación.',
    not_found: 'El recurso solicitado ya no está disponible.',
    conflict: 'La clave ya está asociada a este cliente.',
    rate_limit: `Airtable limitó temporalmente la consulta. Intenta de nuevo${error?.retryAfterSeconds ? ` en ${error.retryAfterSeconds} segundos` : ' más tarde'}.`,
    server: 'El servicio del Catálogo SAT no está disponible.',
    network: 'No fue posible conectar con el Catálogo SAT.',
    validation: error?.message || 'Revisa los datos capturados.'
  };
  return messages[error?.kind] || 'Ocurrió un error al consultar el Catálogo SAT.';
}

const keyCode = item => item?.key || item?.claveSat || item?.clave || '';
const keyDescription = item => item?.description || item?.descripcion || 'Sin descripción';

function Highlight({ text, query }) {
  const value = String(text || '');
  const needle = String(query || '').trim();
  if (!needle) return value;
  const parts = value.split(new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
  return parts.map((part, index) => part.toLocaleLowerCase() === needle.toLocaleLowerCase()
    ? <mark key={`${part}-${index}`}>{part}</mark>
    : part);
}

function groupResults(items) {
  const groups = new Map();
  items.forEach(item => {
    const code = keyCode(item);
    const classKey = item?.classKey || (code ? `${code.slice(0, 6)}00` : '');
    if (!groups.has(classKey)) groups.set(classKey, { classKey, parent: null, children: [] });
    const group = groups.get(classKey);
    const parent = item?.parent || (item?.isClass ? { key: code, description: keyDescription(item) } : null);
    if (parent && !group.parent) group.parent = parent;
    if (item?.isClass || code === parent?.key) {
      group.parent = parent || { key: code, description: keyDescription(item) };
    } else {
      group.children.push(item);
    }
  });
  return [...groups.values()];
}

function validCommonKeys(items) {
  const seen = new Set();
  return items.filter(item => {
    const code = keyCode(item);
    if (!/^\d{8}$/.test(code) || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

export default function CatalogoSatView() {
  const [query, setQuery] = useState('');
  const [soloClase, setSoloClase] = useState(false);
  const [results, setResults] = useState([]);
  const [searchState, setSearchState] = useState('idle');
  const [searchError, setSearchError] = useState('');
  const [clientQuery, setClientQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [clientState, setClientState] = useState('idle');
  const [selectedClient, setSelectedClient] = useState(null);
  const [commonKeys, setCommonKeys] = useState([]);
  const [commonState, setCommonState] = useState('idle');
  const [commonError, setCommonError] = useState('');
  const [pendingKeys, setPendingKeys] = useState(() => new Set());
  const [announcement, setAnnouncement] = useState('');
  const [capabilities, setCapabilities] = useState({ canManageCommonKeys: false });
  const clientSearchController = useRef(null);
  const commonKeysController = useRef(null);
  const selectedClientId = useRef(null);

  const pinnedCommonKeys = useMemo(() => selectedClient ? validCommonKeys(commonKeys) : [], [commonKeys, selectedClient]);
  const pinnedCodes = useMemo(() => new Set(pinnedCommonKeys.map(keyCode)), [pinnedCommonKeys]);
  const regularResults = useMemo(() => results.filter(item => !pinnedCodes.has(keyCode(item))), [pinnedCodes, results]);
  const regularGroups = useMemo(() => groupResults(regularResults).map(group => ({ ...group, parentIsPinned: pinnedCodes.has(group.parent?.key) })), [pinnedCodes, regularResults]);

  useEffect(() => {
    const normalized = query.trim();
    if (!normalized) {
      setResults([]);
      setSearchState('idle');
      setSearchError('');
      return undefined;
    }
    if (!isCatalogoSatQueryValid(normalized)) {
      setResults([]);
      setSearchState('hint');
      setSearchError('');
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchState('loading');
      setSearchError('');
      try {
        const response = await searchCatalogoSat({ query: normalized, soloClase, limit: 20, signal: controller.signal });
        setResults(response.items);
        setCapabilities(response.capabilities);
        setSearchState(response.items.length ? 'ready' : 'empty');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setResults([]);
        setSearchError(errorMessage(error));
        setSearchState('error');
      }
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, soloClase]);

  useEffect(() => {
    const normalized = clientQuery.trim();
    if (normalized.length < 3) {
      clientSearchController.current?.abort();
      setClients([]);
      setClientState(normalized ? 'hint' : 'idle');
      return undefined;
    }
    const controller = new AbortController();
    clientSearchController.current?.abort();
    clientSearchController.current = controller;
    const timer = setTimeout(async () => {
      setClientState('loading');
      try {
        const response = await searchCatalogoSatClients({ query: normalized, signal: controller.signal });
        setClients(response.items);
        setCapabilities(response.capabilities);
        setClientState(response.items.length ? 'ready' : 'empty');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        setClients([]);
        setClientState('error');
      }
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [clientQuery]);

  async function copyKey(code, label) {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(code);
      else {
        const input = document.createElement('textarea');
        input.value = code;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        if (!document.execCommand('copy')) throw new Error('copy_failed');
        input.remove();
      }
      setAnnouncement(`${label} ${code} copiada.`);
    } catch {
      setAnnouncement(`No fue posible copiar ${code}. Selecciona y copia el código manualmente.`);
    }
  }

  async function refreshCommonKeys(client) {
    const controller = new AbortController();
    commonKeysController.current?.abort();
    commonKeysController.current = controller;
    setCommonState('loading');
    setCommonError('');
    try {
      const response = await getCatalogoSatCommonKeys({ clientId: client.recordId, signal: controller.signal });
      if (controller.signal.aborted || selectedClientId.current !== client.recordId) return;
      setCommonKeys(response.items);
      setCapabilities(response.capabilities);
      setCommonState(response.items.length ? 'ready' : 'empty');
    } catch (error) {
      if (error?.name === 'AbortError' || controller.signal.aborted) return;
      if (selectedClientId.current !== client.recordId) return;
      setCommonKeys([]);
      setCommonError(errorMessage(error));
      setCommonState('error');
    } finally {
      if (commonKeysController.current === controller) commonKeysController.current = null;
    }
  }

  async function loadCommonKeys(client) {
    selectedClientId.current = client.recordId;
    setSelectedClient(client);
    setClientQuery('');
    setClients([]);
    await refreshCommonKeys(client);
  }

  function changeClient() {
    commonKeysController.current?.abort();
    selectedClientId.current = null;
    setSelectedClient(null);
    setCommonKeys([]);
    setCommonState('idle');
    setCommonError('');
    setClientQuery('');
  }

  async function addCommon(parent) {
    if (!selectedClient || !parent?.key || pendingKeys.has(parent.key)) return;
    const clientAtStart = selectedClient;
    const code = parent.key;
    setPendingKeys(current => new Set(current).add(code));
    setCommonError('');
    try {
      const response = await addCatalogoSatCommonKey({ clientId: clientAtStart.recordId, claveSat: code });
      if (selectedClientId.current !== clientAtStart.recordId) return;
      setCommonKeys(current => current.some(item => keyCode(item) === code) ? current : [...current, response.item || parent]);
      setCommonState('ready');
      setAnnouncement(`La clase ${code} se añadió a las comunes del cliente.`);
    } catch (error) {
      if (selectedClientId.current !== clientAtStart.recordId) return;
      if (error?.kind === 'conflict' || error?.status === 409) {
        setCommonKeys(current => current.some(item => keyCode(item) === code) ? current : [...current, parent]);
        setAnnouncement(`La clase ${code} ya es común para este cliente.`);
        await refreshCommonKeys(clientAtStart);
      } else setCommonError(errorMessage(error));
    } finally {
      setPendingKeys(current => { const next = new Set(current); next.delete(code); return next; });
    }
  }

  async function removeCommon(item) {
    const code = keyCode(item);
    if (pendingKeys.has(code) || !window.confirm(`¿Retirar la clave común ${code}?`)) return;
    const clientAtStart = selectedClient;
    if (!clientAtStart) return;
    setPendingKeys(current => new Set(current).add(code));
    setCommonError('');
    try {
      await removeCatalogoSatCommonKey({ clientId: clientAtStart.recordId, claveSat: code });
      if (selectedClientId.current !== clientAtStart.recordId) return;
      setCommonKeys(current => current.filter(candidate => keyCode(candidate) !== code));
      setCommonState(pinnedCommonKeys.length <= 1 ? 'empty' : 'ready');
      setAnnouncement(`La clase ${code} se retiró de las comunes del cliente.`);
    } catch (error) {
      setCommonError(errorMessage(error));
    } finally {
      setPendingKeys(current => { const next = new Set(current); next.delete(code); return next; });
    }
  }

  function commonAction(parent, child = false) {
    if (!parent) return <span className="catalogo-sat-unavailable">Clase de referencia no disponible</span>;
    if (!selectedClient) return <span className="catalogo-sat-invitation">Selecciona un cliente para guardar esta clase como común</span>;
    if (!capabilities.canManageCommonKeys) return null;
    if (pinnedCodes.has(parent.key)) return <button type="button" disabled>Ya es común</button>;
    const pending = pendingKeys.has(parent.key);
    return (
      <button type="button" disabled={pending} onClick={() => addCommon(parent)}>
        {pending ? 'Añadiendo…' : child ? `Añadir clase ${parent.key} a comunes` : 'Añadir a comunes'}
      </button>
    );
  }

  return (
    <main className="catalogo-sat-view" aria-labelledby="catalogo-sat-title">
      <header className="catalogo-sat-header">
        <p className="catalogo-sat-eyebrow">Facturación · Catálogo SAT</p>
        <h1 id="catalogo-sat-title">Claves de productos y servicios</h1>
        <p>Encuentra una clase recomendada, revisa coincidencias específicas y personaliza las claves comunes de un cliente.</p>
      </header>

      <div className="catalogo-sat-layout">
        <aside className="catalogo-sat-panel catalogo-sat-client-panel" aria-labelledby="catalogo-sat-client-title">
          <h2 id="catalogo-sat-client-title">Cliente para personalizar</h2>
          {selectedClient ? (
            <div className="catalogo-sat-selected-client">
              <span>Cliente seleccionado</span><strong>{selectedClient.label}</strong><small>{selectedClient.rfc}</small>
              <b>{commonKeys.length} claves comunes</b>
              <button type="button" onClick={changeClient}>Cambiar cliente</button>
            </div>
          ) : (
            <>
              <label htmlFor="catalogo-sat-client-query">Buscar cliente por nombre o RFC</label>
              <input id="catalogo-sat-client-query" className="catalogo-sat-input" type="search" value={clientQuery} onChange={event => setClientQuery(event.target.value)} autoComplete="off" aria-autocomplete="list" />
              <div className="catalogo-sat-status" role="status" aria-live="polite">
                {clientState === 'hint' && 'Escribe al menos 3 caracteres para buscar clientes.'}
                {clientState === 'loading' && 'Buscando clientes…'}
                {clientState === 'empty' && 'No se encontraron clientes activos.'}
                {clientState === 'error' && 'No fue posible consultar clientes.'}
              </div>
              {clients.length > 0 && <ul className="catalogo-sat-client-options" role="listbox" aria-label="Clientes activos">{clients.map(client => <li key={client.recordId}><button type="button" role="option" aria-selected="false" onClick={() => loadCommonKeys(client)}><span>{client.label}</span><small>{client.rfc}</small></button></li>)}</ul>}
            </>
          )}
        </aside>

        <section className="catalogo-sat-panel catalogo-sat-search-panel" aria-labelledby="catalogo-sat-search-title">
          <h2 id="catalogo-sat-search-title">Buscar claves</h2>
          <p>Escribe al menos 3 caracteres o una clave exacta de 8 dígitos.</p>
          <fieldset className="catalogo-sat-scope"><legend>Alcance de búsqueda</legend><label><input type="radio" name="catalogo-sat-scope" checked={!soloClase} onChange={() => setSoloClase(false)} /> Todo el catálogo</label><label><input type="radio" name="catalogo-sat-scope" checked={soloClase} onChange={() => setSoloClase(true)} /> Sólo clases recomendadas</label></fieldset>
          <label htmlFor="catalogo-sat-query">Código o descripción</label>
          <input id="catalogo-sat-query" className="catalogo-sat-input" type="search" value={query} onChange={event => setQuery(event.target.value)} autoComplete="off" />
          <div className="catalogo-sat-status" role="status" aria-live="polite">{searchState === 'hint' && 'Escribe al menos 3 caracteres.'}{searchState === 'loading' && 'Buscando claves…'}{searchState === 'empty' && 'No se encontraron claves.'}{searchState === 'error' && searchError}</div>

          {selectedClient && <section className="catalogo-sat-pinned" aria-labelledby="catalogo-sat-pinned-title">
            <div className="catalogo-sat-pinned-heading"><div><h3 id="catalogo-sat-pinned-title">Claves comunes de {selectedClient.label}</h3><p>Disponibles para este cliente sin alterar tu búsqueda actual.</p></div><span className="catalogo-sat-badge">Ancladas</span><strong aria-label={`${pinnedCommonKeys.length} claves comunes ancladas`}>{pinnedCommonKeys.length}</strong></div>
            <div className="catalogo-sat-status" role="status" aria-live="polite">{commonState === 'loading' && 'Cargando claves comunes…'}{commonState === 'empty' && 'Este cliente todavía no tiene claves comunes.'}{commonError}</div>
            {pinnedCommonKeys.length > 0 && <ul className="catalogo-sat-pinned-list" aria-label="Claves comunes ancladas">{pinnedCommonKeys.map(item => { const code = keyCode(item); return <li key={code}><div><strong>{code}</strong><span>{keyDescription(item)}</span></div><div className="catalogo-sat-actions"><button type="button" onClick={() => copyKey(code, 'Clave')}>Copiar</button><button type="button" className="danger" disabled={pendingKeys.has(code)} onClick={() => removeCommon(item)}>{pendingKeys.has(code) ? 'Retirando…' : 'Retirar'}</button></div></li>; })}</ul>}
          </section>}

          <section className="catalogo-sat-results" aria-labelledby="catalogo-sat-results-title">
            <h3 id="catalogo-sat-results-title">Resultados del catálogo</h3>
          {regularGroups.length > 0 && <div className="catalogo-sat-groups" aria-label="Resultados de claves SAT">{regularGroups.map(group => (
            <article className="catalogo-sat-group" key={group.classKey}>
              {!group.parentIsPinned && <div className="catalogo-sat-parent-card">
                <span className="catalogo-sat-badge">Clase recomendada</span>
                {group.parent ? <><strong className="catalogo-sat-code"><Highlight text={group.parent.key} query={query} /></strong><h3><Highlight text={group.parent.description} query={query} /></h3><p>Esta clase general suele ser suficiente; valida el caso antes de facturar</p><div className="catalogo-sat-actions"><button type="button" onClick={() => copyKey(group.parent.key, 'Clase')}>Copiar clase</button>{commonAction(group.parent)}</div></> : <><strong>{group.classKey}</strong><p>Clase de referencia no disponible</p>{commonAction(null)}</>}
              </div>}
              {group.children.length > 0 && <div className="catalogo-sat-children"><h4>{group.parentIsPinned ? 'Coincidencias específicas de una clase anclada' : 'Coincidencias específicas'}</h4><ul>{group.children.map(child => <li key={keyCode(child)}><div><strong><Highlight text={keyCode(child)} query={query} /></strong><span><Highlight text={keyDescription(child)} query={query} /></span></div><div className="catalogo-sat-actions"><button type="button" className="secondary" onClick={() => copyKey(keyCode(child), 'Clave específica')}>Copiar específica</button>{commonAction(group.parent, true)}</div></li>)}</ul></div>}
            </article>
          ))}</div>}
          </section>
        </section>
      </div>
      <div className="catalogo-sat-announcement" role="status" aria-live="polite">{announcement}</div>
    </main>
  );
}
